import type { HelpContent } from './types';

export const dashboardHelp: HelpContent = {
  pageTitle: 'Homepage',
  description: 'Your personalised homepage showing a summary of portfolio health, project delivery status, and key metrics relevant to your work.',

  functionality: {
    overview:
      'The Homepage is your personalised hub in Access Projects. It loads automatically after login and displays a summary of project health, key metrics, and activity relevant to your work. You can customise which widgets are shown using the settings cog, so the layout reflects what matters most to you.',
    keyFeatures: [
      {
        label: 'Your personalised dashboard',
        detail: 'Your homepage automatically shows the widgets most relevant to your work — no setup or manual switching required.',
      },
      {
        label: 'Widget visibility preferences',
        detail: 'Show or hide individual widgets to customise your homepage layout. Your preferences are saved automatically.',
      },
      {
        label: 'Portfolio health overview',
        detail: 'Get an at-a-glance view across all projects — status, profitability, risk flags, and delivery health in one place.',
      },
      {
        label: 'Project delivery summary',
        detail: 'See your projects\' delivery health, budget status, and team allocations all in one place, without navigating away.',
      },
      {
        label: 'Your project allocation list',
        detail: 'See a focused, up-to-date list of the projects you are currently working on.',
      },
      {
        label: 'Settings cog panel',
        detail: 'Quickly access widget display preferences from the cog icon in the page header.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Page header',
        description: 'Displays a personalised greeting, a subtitle describing the current view, and the settings cog for widget preferences.',
      },
      {
        name: 'Settings cog panel',
        description: 'Opens a panel where you can toggle individual widgets on or off.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Your dashboard',
        description: 'Displays a summary of project health, financials, delivery status, and team allocations relevant to your work. Widgets can be shown or hidden using the settings cog.',
      },
      {
        name: 'KPI / metric cards',
        description: 'Summary cards showing key numbers such as total projects, budget health, utilisation rates, and overdue milestones.',
      },
      {
        name: 'Charts and graphs',
        description: 'Visual breakdowns of portfolio performance, profitability trends, and resource utilisation over time.',
      },
    ],
    useCases: [
      'Getting a quick health check on the portfolio at the start of the day.',
      'Checking the status of your assigned projects before a team standup.',
      'Spotting projects that are over budget or behind schedule without opening each one individually.',
      'Reviewing which projects you are currently allocated to as a team member.',
      'Customising which widgets appear on your homepage to match your daily workflow.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Your homepage loads automatically after login. Widgets are displayed based on your work and no setup is required. If you want to customise which widgets appear, click the cog icon in the top-right of the page.',

    steps: [
      {
        title: 'How do I customise which widgets appear on my homepage?',
        items: [
          {
            text: 'Click the cog icon in the top-right area of the homepage header.',
            note: 'The cog icon is only visible to users with widget customisation enabled.',
          },
          {
            text: 'In the panel that opens, toggle individual widgets on or off using the switches.',
            tip: 'Your preferences are saved automatically — you do not need to click a Save button.',
          },
          {
            text: 'Close the panel by clicking outside it or pressing Escape. The homepage updates immediately.',
          },
        ],
      },
      {
        title: 'How do I navigate to a project from the homepage?',
        items: [
          {
            text: 'Find the project in any of the project-related widgets or the project list.',
          },
          {
            text: 'Click the project name or row to open the Project Details page for that project.',
            tip: 'You can also use the Projects page (left sidebar) for a full searchable list of all projects.',
          },
        ],
      },
      {
        title: 'How do I understand the status indicators on the dashboard?',
        items: [
          {
            text: 'Green indicators mean the project or metric is on track.',
          },
          {
            text: 'Amber indicators flag a project or metric that needs attention — it is approaching a threshold but is not yet critical.',
          },
          {
            text: 'Red indicators mean the project or metric is outside acceptable limits and likely needs immediate action.',
            note: 'The thresholds for amber and red are configured in General Settings. Contact your Administrator if you think the thresholds are incorrect.',
          },
        ],
      },
      {
        title: 'Why does my homepage look different to a colleague\'s?',
        items: [
          {
            text: 'Each homepage is personalised to show the widgets and project data most relevant to you. Your colleague may also have different widget visibility preferences set.',
            note: 'If your homepage doesn\'t look as expected, check your widget preferences via the settings cog, or contact your Administrator.',
          },
        ],
      },
    ],

    proTips: [
      'Use the homepage KPI cards as your morning briefing — if everything is green, you can focus on delivery rather than firefighting.',
      'Hide widgets you rarely use via the cog panel to reduce visual noise and keep focus on what matters to you.',
      'If you work across multiple projects, the delivery health widgets on your homepage give you a quick RAG status for each — saving you from opening every project individually.',
    ],
  },

  faq: [
    {
      question: 'Why can\'t I see all the widgets my colleague can see?',
      answer:
        'Your homepage is personalised to show the widgets most relevant to your work, so it may differ from a colleague\'s. You can also show or hide individual widgets using the settings cog. If you believe your homepage isn\'t displaying correctly, contact your Administrator.',
    },
    {
      question: 'How do I reset my widget preferences back to the default layout?',
      answer:
        'Open the settings cog panel in the page header and re-enable any widgets you have hidden. There is no single "reset to default" button — you need to toggle each widget back on individually.',
    },
    {
      question: 'What does the "Showing your projects only" label mean on a widget?',
      answer:
        'This label indicates that the data shown is scoped to the projects relevant to you — not the full portfolio.',
    },
    {
      question: 'Can I add custom widgets to my homepage?',
      answer:
        'Not currently. The available widgets are fixed, but you can show or hide them using the settings cog to keep your homepage focused on what matters to you.',
    },
    {
      question: 'How do I get back to the homepage from another page?',
      answer:
        'Click "Homepage" in the left sidebar to return to your homepage from anywhere in Access Projects.',
    },
    {
      question: 'What does a red KPI card on the homepage mean?',
      answer:
        'A red card means a metric has exceeded its alert threshold — for example, a project is over budget or past its deadline. Click through to the relevant project or report page for more detail.',
    },
    {
      question: 'Can I see data for all projects?',
      answer:
        'Your homepage displays data scoped to the projects relevant to your work. If you feel you are missing visibility of certain projects, contact your Administrator to review your project assignments.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'Settings', path: '/settings' },
    { label: 'Utilisation', path: '/utilisation' },
  ],
};
